import { DEFAULT_VIEW_SCHEMA_VERSION, type AnyViewEnvelope, type DataTableCellTone, type FlowViewType, type ViewDataMap } from '../../types/views.ts';
import type { ResultPayload, ResultPayloadSection, ResultSectionRole, ResultTabScope } from '../../types/result-composition.ts';
import type { ResultSectionWidth } from '../../result-layout.ts';

/**
 * Layout lab (02/10/2026): app scenarios built from fictional data, rendered by
 * the real Domino views to test how the result layer holds up — creation,
 * freedom, visual consistency — without any model call. Every name here is
 * invented; no real company or person.
 */

export interface AppScenario {
  id: string;
  /** App type, as a user would name it. */
  title: string;
  /** Who uses it and for what decision. */
  job: string;
  /** What this scenario stresses in the layout. */
  stresses: string[];
  /** What the user would expect and Domino cannot show today (gaps to judge). */
  wished: string[];
  payload: ResultPayload;
}

export const envelope = <T extends FlowViewType>(viewType: T, data: ViewDataMap[T]): AnyViewEnvelope =>
  ({ view_type: viewType, schema_version: DEFAULT_VIEW_SCHEMA_VERSION, data }) as AnyViewEnvelope;

export const section = <T extends FlowViewType>(
  id: string,
  label: string,
  role: ResultSectionRole,
  viewType: T,
  data: ViewDataMap[T],
  width?: ResultSectionWidth,
): ResultPayloadSection => ({ id, label, role, viewType, envelope: envelope(viewType, data), ...(width ? { width } : {}) });

export const tab = (id: string, label: string, sections: ResultPayloadSection[], scope: ResultTabScope = 'run') =>
  ({ id, label, scope, sections });

type CellInput = string | number | boolean | null;

/** A cell with its reading (colored dot), for selections and comparison matrices. */
export const toned = (value: CellInput, tone: DataTableCellTone) => ({ value, tone });

/** A data_table from rows written as plain records, columns in the given order. */
export const table = (
  title: string,
  columns: Array<[key: string, label: string]>,
  rows: Array<Record<string, CellInput | ReturnType<typeof toned>>>,
  extra: { empty_message?: string; source_note?: string } = {},
): ViewDataMap['data_table'] => ({
  title,
  columns: columns.map(([key, label]) => ({ key, label })),
  rows: rows.map((row, index) => ({
    id: String(index + 1),
    cells: columns.map(([key]) => {
      const cell = row[key] ?? null;
      return cell !== null && typeof cell === 'object' ? { key, ...cell } : { key, value: cell };
    }),
  })),
  ...extra,
});

/** Deterministic pseudo-random picker, so the lab renders the same every time. */
export const picker = (seed: number) => {
  let state = seed;
  const next = () => {
    state = (state * 1103515245 + 12345) % 2147483648;
    return state / 2147483648;
  };
  return {
    next,
    pick: <T>(values: readonly T[]): T => values[Math.floor(next() * values.length)]!,
    int: (min: number, max: number) => min + Math.floor(next() * (max - min + 1)),
  };
};
